import { RIDER_OPTIONS } from "#src/data/quoteParams.js";

import {
  CONCEPTS,
  PRODUCTS,
  ProductType,
  UL_TYPES,
} from "#src/structures/ProductType.js";
import { useInstanceSettingsStore } from "#src/stores/instance-settings.js";
import { useQuotingStore } from "#src/stores/quoting.js";
import {
  boolOrNull,
  numOrNull,
  searchToArray,
  strToBoolean,
  valOrNull,
} from "#src/util/helpers.js";
import { SUPPORTED_MODES } from "#src/data/modes.js";
import {
  RIDER_PERCENTAGE_OPTIONS,
  INCOME_SOLVE_OPTIONS,
} from "#src/data/quoteParams.js";
import { STATES } from "#src/data/states-and-countries.js";
import {
  enforceStrictParameterValidation,
  quoteSetDefaultsAndValidate,
} from "#src/structures/quote-params/QuoteParameterValidation.js";

export class QuotingPrefillService {
  #pinia;

  constructor(pinia) {
    this.#pinia = pinia;
  }

  setRequestPreferencesFromPrefill(defaults) {
    const quotingStore = useQuotingStore(this.#pinia);

    const prefill = this.normalize(defaults);

    if (prefill.carriers)
      quotingStore.requestPreferences.carriers = prefill.carriers;
    if (prefill.products)
      quotingStore.requestPreferences.products = prefill.products;
  }

  setStrictProductPrefill(defaults) {
    const quotingStore = useQuotingStore(this.#pinia);
    const prefill = this.normalize(defaults);

    Object.keys(prefill).forEach(key => {
      const isDefined = ![undefined, null].includes(prefill[key]);
      if (isDefined) quotingStore.params[key] = prefill[key];
    });

    const validation = quoteSetDefaultsAndValidate(
      this.#pinia,
      quotingStore.params
    );
    enforceStrictParameterValidation(validation);

    Object.keys(validation).forEach(v => {
      if (v in quotingStore.params) {
        quotingStore.params[v] = validation[v].value;
      }
    });
  }

  setPrefillWithFallback(defaults) {
    const quotingStore = useQuotingStore(this.#pinia);
    const instanceSettings = useInstanceSettingsStore(this.#pinia);

    const prefill = this.normalize(defaults);

    if (prefill.premium) quotingStore.params.premium = prefill.premium;
    if (prefill.income_start_age)
      quotingStore.params.income_start_age = prefill.income_start_age;
    if (prefill.income_end_age)
      quotingStore.params.income_end_age = prefill.income_end_age;
    if (prefill.pay_duration)
      quotingStore.params.pay_duration = prefill.pay_duration;
    if (prefill.cash_value) quotingStore.params.cash_value = prefill.cash_value;
    if (prefill.guaranteed_issue)
      quotingStore.params.guaranteed_issue = prefill.guaranteed_issue;

    if (prefill.lapse_protection_to_age) {
      quotingStore.params.lapse_protection_to_age =
        prefill.lapse_protection_to_age;
    }

    if (INCOME_SOLVE_OPTIONS.includes(prefill.income_solve)) {
      quotingStore.params.income_solve = prefill.income_solve;
    }

    if (RIDER_OPTIONS.includes(prefill.filter_chronic_illness_and_ltc)) {
      quotingStore.params.filter_chronic_illness_and_ltc =
        prefill.filter_chronic_illness_and_ltc;
    }

    if (RIDER_PERCENTAGE_OPTIONS.includes(prefill.ltc_rider_percentage)) {
      quotingStore.params.ltc_rider_percentage = prefill.ltc_rider_percentage;
    }

    if (SUPPORTED_MODES.includes(prefill.mode)) {
      quotingStore.params.mode = prefill.mode;
    }

    if (prefill.selected_type) {
      const enabledProduct =
        instanceSettings.products[prefill.selected_type] ||
        instanceSettings.concepts[prefill.selected_type];

      if (enabledProduct) {
        quotingStore.params.selected_type = prefill.selected_type;

        if (prefill.subtype) {
          const enabledSubtype = instanceSettings.products[prefill.subtype];
          if (enabledSubtype) quotingStore.params.subtype = prefill.subtype;
        }

        const product = new ProductType(
          quotingStore.params.subtype || quotingStore.params.selected_type
        );

        if (prefill.solve) {
          if (product.solves) {
            const allSolves = [];
            Object.keys(product.solves).forEach(solveType => {
              allSolves.push(...product.solves[solveType]);
            });

            const isValidSolve = allSolves.some(({ text, value }) =>
              [text, value].includes(prefill.solve)
            );

            if (isValidSolve) quotingStore.params.solve = prefill.solve;
          }
        }

        if (prefill.active_years) {
          const { active_years } = product.eAppParser({
            active_years: prefill.active_years,
          });

          quotingStore.params.active_years = active_years;
        }
      }
    }

    if (prefill.face_amount) {
      quotingStore.params.face_amount = prefill.face_amount;
    }

    if (prefill.income_amount) {
      quotingStore.params.income_amount = prefill.income_amount;
    }

    //state setting
    const ipState = quotingStore.params.state;
    if (prefill.application_state)
      quotingStore.params.state = prefill.application_state;
    else if (prefill.state) quotingStore.params.state = prefill.state;
    if (quotingStore.params.state) {
      const isLicensedInState = instanceSettings.agent.insurance_licenses.some(
        license => license.state === quotingStore.params.state
      );

      if (!isLicensedInState) {
        quotingStore.params.state = ipState;
      }
    }
  }

  // keep public for test
  normalize(initPrefill) {
    const { selected_type, subtype } = this.#normalizeType(initPrefill);
    const carriers = this.#normalizeCarriers(initPrefill);
    const products = this.#normalizeProducts(initPrefill);
    const face_amount = this.#normalizeFaceAmount(initPrefill);
    const application_state = this.#normalizeState(
      initPrefill.application_state
    );
    const state = this.#normalizeState(initPrefill.state) || application_state;

    return {
      age_value: numOrNull(initPrefill.age_value),
      application_state,
      benefit_period: numOrNull(initPrefill.benefit_period),
      carriers,
      cash_value: numOrNull(initPrefill.cash_value),
      crediting_rate: valOrNull(initPrefill.crediting_rate),
      discount: valOrNull(initPrefill.discount),
      elimination_period: numOrNull(initPrefill.elimination_period),
      face_amount,
      filter_chronic_illness_and_ltc: valOrNull(
        initPrefill.chronic_illness_and_ltc
      ),
      guaranteed_issue: strToBoolean(initPrefill.guaranteed_issue),
      home_health_care_waiver: strToBoolean(
        initPrefill.home_health_care_waiver
      ),
      income_amount: numOrNull(initPrefill.income_amount),
      income_end_age: numOrNull(initPrefill.income_end_age),
      income_solve: initPrefill.income_solve,
      income_start_age: numOrNull(initPrefill.income_start_age),
      inflation_percentage: numOrNull(initPrefill.inflation_percentage),
      joint_waiver_of_premium: strToBoolean(
        initPrefill.joint_waiver_of_premium
      ),
      joint_income: strToBoolean(initPrefill.joint_income),
      lapse_protection_to_age: numOrNull(initPrefill.lapse_protection_to_age),
      living_benefits: boolOrNull(initPrefill.living_benefits),
      loan_type: valOrNull(initPrefill.loan_type),
      ltc_rider_percentage: numOrNull(initPrefill.ltc_rider_percentage),
      mode: numOrNull(initPrefill.mode),
      monthly_benefit: numOrNull(initPrefill.monthly_benefit),
      partner_discount: valOrNull(initPrefill.partner_discount),
      pay_duration: numOrNull(initPrefill.pay_duration),
      pool_of_money: numOrNull(initPrefill.pool_of_money),
      use_pool_of_money:
        strToBoolean(initPrefill.use_pool_of_money) ||
        Boolean(initPrefill.use_pool_of_money && !initPrefill.benefit_period),
      premium: numOrNull(initPrefill.premium),
      products,
      return_of_premium_rider: valOrNull(initPrefill.return_of_premium_rider),
      save_age: boolOrNull(initPrefill.save_age),
      section_1035_exchange_amount: numOrNull(
        initPrefill.section_1035_exchange_amount
      ),
      selected_type,
      shared_care: strToBoolean(initPrefill.shared_care),
      solve: valOrNull(initPrefill.solve),
      state,
      subtype,
      active_years:
        numOrNull(initPrefill.term_duration) ||
        numOrNull(initPrefill.active_years), // term_duration is a legacy key for api users
      vitality_level: valOrNull(initPrefill.vitality_level),
    };
  }

  #normalizeType(initPrefill) {
    let selected_type, subtype;
    if (!initPrefill.selected_type) return { selected_type, subtype };
    const cleanProduct = v => v.toLowerCase().replaceAll("_", "");

    const cleanedSelectedType = cleanProduct(initPrefill.selected_type);
    const supportedProducts = [
      ...Object.values(PRODUCTS),
      ...Object.values(CONCEPTS),
    ];

    let supportedProduct;
    supportedProducts.forEach(type => {
      const cleanedType = cleanProduct(type);
      if (cleanedSelectedType === cleanedType) supportedProduct = type;
    });

    selected_type = supportedProduct;

    if (UL_TYPES.includes(selected_type)) {
      selected_type = CONCEPTS.ALL_UL;
      subtype = initPrefill.selected_type;
    }

    return { selected_type, subtype };
  }

  #normalizeCarriers(initPrefill) {
    if (!initPrefill.carriers) return;
    let setCarriers = initPrefill.carriers;
    if (typeof setCarriers === "string") {
      setCarriers = searchToArray(setCarriers, { isNumeric: true });
    }

    if (Array.isArray(setCarriers)) return setCarriers;
  }

  #normalizeProducts(initPrefill) {
    if (!initPrefill.products) return;
    let setProducts = initPrefill.products;
    if (typeof setProducts === "string") {
      setProducts = searchToArray(setProducts, { isNumeric: true });
    }

    if (Array.isArray(setProducts)) return setProducts;
  }

  #normalizeFaceAmount(initPrefill) {
    if (numOrNull(initPrefill.death_benefit)) {
      return +initPrefill.death_benefit;
    }
    if (numOrNull(initPrefill.term_face_amount)) {
      return +initPrefill.term_face_amount;
    }
  }

  #normalizeState(value) {
    if (!value) return;
    let state = `${value}`.toUpperCase();
    if (STATES.some(s => s.value === state)) return state;
  }
}
